import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Waves } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", parent_email: "", beach_location: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.parent_email) delete payload.parent_email;
      if (!payload.beach_location) delete payload.beach_location;
      const u = await register(payload);
      toast.success(`Account created — welcome, ${u.name}!`);
      nav(u.role === "parent" ? "/parent" : "/student");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(", ") : (typeof d === "string" ? d : "Registration failed");
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 stripe">
      <div className="hidden lg:flex flex-col justify-center px-16">
        <div className="flex items-center gap-2 mb-8">
          <span className="grid place-items-center w-10 h-10 rounded-2xl bg-teal-700 text-white"><Waves size={20} strokeWidth={2.5} /></span>
          <span className="font-display text-2xl font-bold text-slate-900">Oceanic Learn</span>
        </div>
        <h2 className="font-display text-4xl font-bold text-slate-900 leading-tight">Start with two missions a day.<br/><span className="text-teal-700">Finish stronger every week.</span></h2>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
          <h1 className="font-display text-2xl font-bold text-slate-900">Create account</h1>
          <div className="mt-6 grid grid-cols-2 gap-2 p-1 rounded-full bg-slate-100">
            {["student","parent"].map((r) => (
              <button key={r} type="button" data-testid={`role-${r}`}
                onClick={()=>set("role", r)}
                className={`py-2 rounded-full text-sm font-semibold transition-colors ${form.role===r ? "bg-white text-teal-800 shadow-sm" : "text-slate-500"}`}>
                {r === "student" ? "Student" : "Parent / Teacher"}
              </button>
            ))}
          </div>

          <label className="block mt-4 text-sm font-medium text-slate-700">Name</label>
          <input data-testid="reg-name" required value={form.name} onChange={(e)=>set("name",e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />

          <label className="block mt-3 text-sm font-medium text-slate-700">Email</label>
          <input data-testid="reg-email" type="email" required value={form.email} onChange={(e)=>set("email",e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />

          <label className="block mt-3 text-sm font-medium text-slate-700">Password</label>
          <input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={(e)=>set("password",e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />

          {form.role === "student" && (
            <>
              <label className="block mt-3 text-sm font-medium text-slate-700">Parent's email (optional)</label>
              <input data-testid="reg-parent-email" type="email" value={form.parent_email} onChange={(e)=>set("parent_email",e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
              <label className="block mt-3 text-sm font-medium text-slate-700">Your local beach (for Beach Lab)</label>
              <input data-testid="reg-beach" value={form.beach_location} onChange={(e)=>set("beach_location",e.target.value)} placeholder="e.g., Cocoa Beach, FL" className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
            </>
          )}

          <button data-testid="reg-submit" disabled={loading} type="submit"
            className="mt-6 w-full py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold transition-colors disabled:opacity-60">
            {loading ? "Creating…" : "Create account"}
          </button>
          <p className="mt-4 text-sm text-slate-500 text-center">
            Have one? <Link to="/login" data-testid="reg-to-login" className="text-teal-700 font-semibold">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
